import { useEffect, useState } from "react";
import { useAdmin, adminAxios } from "./AdminContext";
import { Settings as SettingsIcon, CheckCircle2, AlertCircle, ShieldCheck, Instagram, Linkedin, Facebook, Youtube } from "lucide-react";

const SOCIAL_FIELDS = [
  { key: "social_instagram", label: "Instagram URL", icon: Instagram, placeholder: "https://instagram.com/..." },
  { key: "social_linkedin", label: "LinkedIn URL", icon: Linkedin, placeholder: "https://linkedin.com/company/..." },
  { key: "social_facebook", label: "Facebook URL", icon: Facebook, placeholder: "https://facebook.com/..." },
  { key: "social_x", label: "X (Twitter) URL", icon: () => <span className="text-xs font-bold">𝕏</span>, placeholder: "https://x.com/..." },
  { key: "social_youtube", label: "YouTube URL", icon: Youtube, placeholder: "https://youtube.com/@..." },
];

export default function SiteSettings() {
  const { token } = useAdmin();
  const [data, setData] = useState({});
  const [form, setForm] = useState({
    turnstile_site_key: "", turnstile_secret_key: "", contact_email: "",
    social_instagram: "", social_linkedin: "", social_facebook: "", social_x: "", social_youtube: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const load = () => {
    adminAxios(token).get("/admin/settings").then(r => {
      setData(r.data);
      setForm(f => ({
        ...f,
        turnstile_site_key: r.data.turnstile_site_key || "",
        turnstile_secret_key: "",
        contact_email: r.data.contact_email || "",
        social_instagram: r.data.social_instagram || "",
        social_linkedin: r.data.social_linkedin || "",
        social_facebook: r.data.social_facebook || "",
        social_x: r.data.social_x || "",
        social_youtube: r.data.social_youtube || "",
      }));
    }).catch(() => setStatus({ state: "error", msg: "Ayarlar yüklenemedi." }));
  };
  useEffect(load, [token]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    const payload = {};
    if (form.turnstile_site_key !== (data.turnstile_site_key || "")) payload.turnstile_site_key = form.turnstile_site_key;
    if (form.turnstile_secret_key) payload.turnstile_secret_key = form.turnstile_secret_key;
    if (form.contact_email && form.contact_email !== data.contact_email) payload.contact_email = form.contact_email;
    SOCIAL_FIELDS.forEach(({ key }) => {
      if ((form[key] || "") !== (data[key] || "")) payload[key] = form[key] || "";
    });
    if (Object.keys(payload).length === 0) {
      setStatus({ state: "error", msg: "Değişiklik yok." }); return;
    }
    try {
      await adminAxios(token).put("/admin/settings", payload);
      setStatus({ state: "success", msg: "Ayarlar kaydedildi." });
      setForm(f => ({ ...f, turnstile_secret_key: "" }));
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Hata oluştu." });
    }
  };

  return (
    <div className="max-w-2xl space-y-8" data-testid="admin-site-settings">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><SettingsIcon size={26} /> Site Ayarları</h1>
        <p className="text-white/50 mt-2">Cloudflare Turnstile, e-posta ve sosyal medya bağlantılarını buradan yönetin.</p>
      </div>

      <form onSubmit={submit} className="space-y-8" data-testid="admin-settings-form">
        {/* Group: Güvenlik */}
        <Section title="Güvenlik & E-posta">
          <Field label="Turnstile Site Key (public)" testId="turnstile-site-key">
            <input type="text" value={form.turnstile_site_key}
              onChange={(e) => setForm({ ...form, turnstile_site_key: e.target.value })}
              className="input-dark-admin" placeholder="0x4AAAAAA..." />
          </Field>
          <Field label="Turnstile Secret Key (gizli — yalnızca güncellemek için doldurun)" testId="turnstile-secret-key">
            <input type="password" value={form.turnstile_secret_key}
              onChange={(e) => setForm({ ...form, turnstile_secret_key: e.target.value })}
              className="input-dark-admin" placeholder={data.turnstile_secret_key_set ? "•••••••• (kayıtlı)" : "Henüz kayıtlı değil"} autoComplete="new-password" />
            {data.turnstile_secret_key_set && (
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#4ADE80]"><ShieldCheck size={12} /> Kayıtlı secret key var</div>
            )}
          </Field>
          <Field label="Admin E-posta (yorum & form bildirimleri buraya gelir)" testId="contact-email">
            <input type="email" value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="input-dark-admin" placeholder="ornek@pratikteknoloji.tr" />
          </Field>
        </Section>

        {/* Group: Sosyal Medya */}
        <Section title="Sosyal Medya Bağlantıları" subtitle="Boş bırakılan kanal alt bilgide gizlenir.">
          <div className="space-y-4">
            {SOCIAL_FIELDS.map(({ key, label, icon: Icon, placeholder }) => (
              <Field key={key} label={label} testId={`field-${key}`}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                    <Icon size={16} />
                  </span>
                  <input type="url" value={form[key]}
                    data-testid={`input-${key}`}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="input-dark-admin pl-10" placeholder={placeholder} />
                </div>
              </Field>
            ))}
          </div>
        </Section>

        {status.state === "success" && (
          <div className="flex items-start gap-2 rounded-xl bg-[#34C759]/10 border border-[#34C759]/30 p-3 text-sm text-[#4ADE80]" data-testid="admin-settings-success">
            <CheckCircle2 size={16} className="mt-0.5" />{status.msg}
          </div>
        )}
        {status.state === "error" && (
          <div className="flex items-start gap-2 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-3 text-sm text-[#FF6B63]" data-testid="admin-settings-error">
            <AlertCircle size={16} className="mt-0.5" />{status.msg}
          </div>
        )}

        <button type="submit" disabled={status.state === "loading"}
          data-testid="admin-settings-submit"
          className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-3 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60">
          {status.state === "loading" ? "Kaydediliyor..." : "Ayarları Kaydet"}
        </button>
      </form>

      <style>{`.input-dark-admin{width:100%;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.12);border-radius:12px;padding:12px 14px;color:#fff;font-size:14px;outline:none;transition:.2s}.input-dark-admin:focus{border-color:#FF3B30;background:rgba(255,255,255,.08)}`}</style>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-8 space-y-5">
      <div>
        <h2 className="font-display text-lg font-bold text-white">{title}</h2>
        {subtitle && <p className="text-xs text-white/50 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block text-xs font-bold tracking-[0.2em] uppercase text-white/50 mb-2">{label}</span>
      {children}
    </label>
  );
}
